import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import type { AuthUser } from "../../types/authUser";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import {
  CATALOGUE,
  CatalogueEntry,
  getAvailableEntries,
  HandoffTarget,
} from "../../domain/askNoticeboard/catalogue";
import { createEmbeddingMatcher } from "../../domain/askNoticeboard/embeddingMatcher";
import {
  confidenceFor,
  type MatchConfidence,
  type MatchThresholds,
  type QuestionMatcher,
} from "../../domain/askNoticeboard/questionMatcher";
import { createWordOverlapMatcher } from "../../domain/askNoticeboard/wordOverlapMatcher";
import {
  replyTo,
  resumeTaskReply,
  exampleQuestion,
  AskedQuestion,
  AssistantReply,
  LOAD_FAILED_REPLY,
  openedReply,
} from "../../domain/askNoticeboard/replyTo";
import type { SearchResult } from "../../hooks/useInstantSearch";
import { CANCELLED_REPLY, handedOver, type TaskRecord } from "../../domain/askNoticeboard/tasks/taskConversation";
import { parkedAfter } from "../../domain/askNoticeboard/tasks/parkedTasks";
import { loadModelEmbed } from "../../services/sentenceEmbedding";
import AskNoticeboardModal from "./AskNoticeboardModal";
import AskNoticeboardBoundary from "./AskNoticeboardBoundary";
import { useOptionalSnackbar } from "../../context/SnackbarContext";
import BackToChatButton from "./BackToChatButton";
import { api } from "../../services/api";
import { redactQuestion } from "../../domain/askNoticeboard/redactQuestion";
import { capChatMessages } from "./chatLog";
import { isAskNoticeboardShortcut } from "./shortcut";
import { AskNoticeboardContext } from "./useAskNoticeboard";

/** A conversation id the save endpoint will accept. `crypto.randomUUID` is missing in some test environments. */
function newConversationId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") return globalThis.crypto.randomUUID();
  const hex = (n: number) => Array.from({ length: n }, () => Math.floor(Math.random() * 16).toString(16)).join("");
  return `${hex(8)}-${hex(4)}-4${hex(3)}-8${hex(3)}-${hex(12)}`;
}

/** Names already loaded for this school: Users, Team members, and children on a family index. */
function knownNames(queryClient: { getQueryCache: () => { getAll: () => { queryKey: readonly unknown[]; state: { data: unknown } }[] } }): string[] {
  const names: string[] = [];
  for (const query of queryClient.getQueryCache().getAll()) {
    const root = query.queryKey[0];
    if (root !== "users" && root !== "teamMembers" && root !== "team" && root !== "family") continue;
    collectNames(query.state.data, names);
  }
  return names;
}

function collectNames(data: unknown, names: string[]): void {
  if (!data || typeof data !== "object") return;
  if (Array.isArray(data)) {
    data.forEach((item) => collectNames(item, names));
    return;
  }
  const record = data as { name?: unknown; users?: unknown; members?: unknown; children?: unknown };
  if (typeof record.name === "string") names.push(record.name);
  if (record.users) collectNames(record.users, names);
  if (record.members) collectNames(record.members, names);
  if (record.children) collectNames(record.children, names);
}

/** The catalogue question, the options shown, the detail still being asked for, and how sure the match was. */
function turnDetails(
  reply: AssistantReply,
  thresholds: MatchThresholds
): { answeredAs?: string; offered?: string[]; awaiting?: string; topScore?: number; confidence?: MatchConfidence } {
  const id = "asked" in reply && reply.asked ? reply.asked.id : undefined;
  const entry = id ? CATALOGUE.find((item) => item.id === id) : undefined;
  const keepsQuestion =
    reply.kind === "answer" || reply.kind === "handoff" || reply.kind === "askSlot" || reply.kind === "loadFailed";
  const answeredAs = entry && keepsQuestion ? exampleQuestion(entry) : undefined;
  const offered =
    reply.kind === "didYouMean" || reply.kind === "unanswered"
      ? reply.questions
      : reply.kind === "askSlot"
        ? reply.choices
        : undefined;
  const awaiting = reply.kind === "askSlot" ? reply.asked.awaiting : undefined;
  const topScore =
    reply.kind !== "safeguarding" && reply.kind !== "searchOnly" && typeof reply.topScore === "number"
      ? reply.topScore
      : undefined;
  return {
    ...(answeredAs ? { answeredAs } : {}),
    ...(offered && offered.length > 0 ? { offered: offered.slice(0, 6) } : {}),
    ...(awaiting ? { awaiting } : {}),
    ...(topScore !== undefined ? { topScore, confidence: confidenceFor(topScore, thresholds) } : {}),
  };
}

function slotsOf(reply: AssistantReply): Record<string, string> {
  if (!("asked" in reply) || !reply.asked?.slots) return {};
  return Object.fromEntries(
    Object.entries(reply.asked.slots).filter((entry): entry is [string, string] => typeof entry[1] === "string")
  );
}

export type ChatMessage =
  | { id: number; role: "user"; text: string }
  | { id: number; role: "assistant"; reply: AssistantReply; revealed: boolean };

export /** The model is tried this many times after the first, then the chat answers by shared words. */
const LAST_LOAD_ATTEMPT = 2;

type MatcherStatus = "loading" | "ready" | "failed";

/** Who the chat belongs to: a different person or Organization starts afresh. */
const sessionOf = (user: AuthUser | null) =>
  user ? [user.userType, user.id ?? user.email ?? user.username, user.schoolId ?? user.orgId].join("|") : null;

type Props = {
  children: React.ReactNode;
  /** The seam for tests: real users get the in-browser model */
  createMatcher?: (entries: CatalogueEntry[]) => QuestionMatcher;
};

const createModelMatcher = (entries: CatalogueEntry[]) =>
  createEmbeddingMatcher(entries, loadModelEmbed);

/**
 * Owns Ask Noticeboard: the ⌘K / Ctrl+K shortcut, the modal and the chat.
 * The chat lives in memory only, so it survives closing the modal but not
 * New chat, logout or a page reload. Alongside it, the last question asked is
 * remembered, including the titles that answer named, so "what about last month?"
 * can ask it again and "When is Dinner at Artistry?" can be about one of those
 * titles. A guided task under way (a Notice being put together) is remembered too,
 * never written to storage. Open to review
 * closes the chat on the task's screen, where Back to chat reopens it. Closing
 * the modal mid-task and reopening it offers Carry on or Start fresh.
 *
 * Messages are answered one at a time, in the order sent, so each reply hears
 * the task and last question the one before it left. New chat, logout,
 * someone else logging in and unmounting drop any reply still on its way.
 */
export function AskNoticeboardProvider({
  children,
  createMatcher = createModelMatcher,
}: Props) {
  const { user, token } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isAiEnabled } = useFeatureFlags();
  const isAvailable = Boolean(user) && isAiEnabled;

  const [isOpen, setIsOpen] = useState(false);
  // How many times it's been opened: a crash inside the chat is cleared on the next open
  const [openCount, setOpenCount] = useState(0);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const nextId = useRef(0);
  const conversationId = useRef(newConversationId());
  const turnNumber = useRef(0);
  const rotation = useRef({ answers: 0, offTopic: 0, voice: 0 });
  const [lastAskedState, setLastAskedState] = useState<AskedQuestion | null>(null);
  // The follow-up as the next reply should hear it, ahead of the next render
  const lastAsked = useRef<AskedQuestion | null>(null);
  const setLastAsked = useCallback((next: AskedQuestion | null) => {
    lastAsked.current = next;
    setLastAskedState(next);
  }, []);
  const [task, setTaskState] = useState<TaskRecord | null>(null);
  // The task as the next reply should hear it, ahead of the next render
  const taskNow = useRef<TaskRecord | null>(null);
  const setTask = useCallback((next: TaskRecord | null) => {
    taskNow.current = next;
    setTaskState(next);
  }, []);
  // Drafts left part-way, newest first, so "carry on with my notice" can pick one up again
  const parked = useRef<TaskRecord[]>([]);
  // Each reply waits for the one before it. Bumping the generation drops the ones still waiting.
  const turns = useRef<Promise<void>>(Promise.resolve());
  const generation = useRef(0);
  const [replying, setReplying] = useState(0);
  // The screen a handoff opened, while the person is still on it
  const [handoffPath, setHandoffPath] = useState<string | null>(null);
  // Set on close, while a task is still being put together; read (once) on the next open
  const pendingResume = useRef<TaskRecord | null>(null);
  // Offered on reopening with that same task still in progress: Carry on or Start fresh
  const [resumeOffer, setResumeOffer] = useState<TaskRecord | null>(null);

  const available = useMemo(() => getAvailableEntries(user), [user]);
  const matcher = useRef<QuestionMatcher | null>(null);
  const [matcherStatus, setMatcherStatus] = useState<MatcherStatus>("loading");
  const [loadAttempt, setLoadAttempt] = useState(0);
  // Answers by shared words until the model is ready, or if it never loads
  const fallbackMatcher = useMemo(() => createWordOverlapMatcher(CATALOGUE), []);
  // Every try at the model failed: the chat still opens, answering by shared words, rather than never
  const [gaveUpOnModel, setGaveUpOnModel] = useState(false);
  const canOpen = matcherStatus === "ready" || gaveUpOnModel;

  // Starts as soon as Ask Noticeboard is available, so the button can wait
  // for the model and arrive only once questions can be answered properly.
  // A failed load is tried again, up to three times; after that the chat answers by shared words.
  useEffect(() => {
    if (!isAvailable) {
      matcher.current = null;
      setMatcherStatus("loading");
      setLoadAttempt(0);
      setGaveUpOnModel(false);
      return;
    }
    let cancelled = false;
    const next = createMatcher(CATALOGUE);
    matcher.current = next;
    const markReady = () => {
      if (!cancelled && matcher.current === next) setMatcherStatus("ready");
    };
    const markFailed = (error: unknown) => {
      console.error("[Ask Noticeboard] Couldn't load the matching model", error);
      if (cancelled || matcher.current !== next) return;
      matcher.current = null;
      setMatcherStatus("failed");
      if (loadAttempt >= LAST_LOAD_ATTEMPT) setGaveUpOnModel(true);
      else setLoadAttempt(loadAttempt + 1);
    };
    if (next.loaded) {
      markReady();
    } else {
      setMatcherStatus("loading");
      next.ready.then(markReady, markFailed);
    }
    return () => {
      cancelled = true;
    };
  }, [isAvailable, createMatcher, loadAttempt]);

  const open = useCallback(() => {
    if (!isAvailable || !canOpen) return;
    // Still the same task that was left running when the modal closed: offer to carry on
    if (pendingResume.current && pendingResume.current === taskNow.current) setResumeOffer(pendingResume.current);
    pendingResume.current = null;
    setOpenCount((n) => n + 1);
    setIsOpen(true);
  }, [isAvailable, canOpen]);
  const close = useCallback(() => {
    pendingResume.current = taskNow.current?.phase !== "done" ? taskNow.current : null;
    setIsOpen(false);
  }, []);
  const clearChat = useCallback(() => {
    generation.current += 1;
    turns.current = Promise.resolve();
    conversationId.current = newConversationId();
    turnNumber.current = 0;
    setReplying(0);
    pendingResume.current = null;
    setMessages([]);
    setLastAsked(null);
    setTask(null);
    parked.current = [];
    setHandoffPath(null);
    setResumeOffer(null);
  }, [setTask, setLastAsked]);

  const openHandoff = useCallback(
    (to: HandoffTarget) => {
      setIsOpen(false);
      setHandoffPath(to.pathname);
      navigate(to.pathname, { state: to.state });
    },
    [navigate]
  );

  /** Looks good on a task's summary: the task is handed over, and its screen opens. */
  const confirmTask = useCallback(
    (to: HandoffTarget) => {
      if (taskNow.current) setTask(handedOver(taskNow.current));
      openHandoff(to);
    },
    [openHandoff, setTask]
  );

  useEffect(() => {
    if (handoffPath && pathname !== handoffPath) setHandoffPath(null);
  }, [handoffPath, pathname]);

  // Logging out or in as someone else clears the chat before paint, so the next person can't read it.
  // The query cache is cleared only when the person actually changes, not on the first render.
  const session = sessionOf(user);
  const seenSession = useRef<string | null | undefined>(undefined);
  useLayoutEffect(() => {
    const previous = seenSession.current;
    seenSession.current = session;
    const switched = previous !== undefined && previous !== session;
    if (switched) queryClient.clear();
    if (previous === undefined || switched) {
      clearChat();
      setIsOpen(false);
    }
  }, [session, clearChat, queryClient]);

  useEffect(
    () => () => {
      generation.current += 1;
    },
    []
  );

  useEffect(() => {
    if (!isAvailable) {
      setIsOpen(false);
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (isAskNoticeboardShortcut(e)) {
        e.preventDefault();
        open();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isAvailable, open]);

  // What a reply hears when its turn comes, not when it was sent: the task and matcher may have moved on
  const latest = useRef({ available, fallbackMatcher, matcherStatus, queryClient, token, user });
  useLayoutEffect(() => {
    latest.current = { available, fallbackMatcher, matcherStatus, queryClient, token, user };
  });

  const replyInTurn = useCallback(
    async (sent: { question: string; questionId: number; pathname: string; generation: number }) => {
      const { available, fallbackMatcher, matcherStatus, queryClient, token, user } = latest.current;
      const task = taskNow.current;
      const activeMatcher = (matcherStatus === "ready" && matcher.current) || fallbackMatcher;
      let reply: AssistantReply;
      try {
        reply = await replyTo(sent.question, {
          available,
          matcher: activeMatcher,
          // The page it was asked on, even if an earlier reply's screen has opened since
          ctx: { user, token, queryClient, pathname: sent.pathname },
          rotation: { ...rotation.current },
          previous: lastAsked.current,
          task,
          usingFallback: matcherStatus !== "ready",
          parked: parked.current,
        });
      } catch (error) {
        console.error("[Ask Noticeboard] Couldn't reply", error);
        reply = { kind: "loadFailed", text: LOAD_FAILED_REPLY };
      }
      if (sent.generation !== generation.current) return;
      parked.current = parkedAfter(parked.current, task, reply);
      if (reply.dropTask) setTask(null);
      if (reply.kind === "task") {
        // The task's own follow-ups replace the last question's
        setTask(reply.task);
        setLastAsked(null);
      } else if (reply.kind === "cancelled") {
        // A cancelled handoff only takes the very next message as a follow-up
        setLastAsked(null);
      } else {
        // A handed-over task only takes the very next message as a follow-up
        if (task?.phase === "done") setTask(null);
        if ("asked" in reply && reply.asked) setLastAsked(reply.asked);
      }
      if (reply.kind === "answer") rotation.current.answers += 1;
      if (reply.kind === "offTopic") rotation.current.offTopic += 1;
      rotation.current.voice += 1;
      if (reply.kind === "answer" && reply.openNow) {
        setIsOpen(false);
        navigate(reply.link.href, { state: reply.link.state });
      }
      const message: ChatMessage = { id: nextId.current++, role: "assistant", reply, revealed: false };
      // Under its own question: later messages may already be waiting below it
      setMessages((prev) => {
        const at = prev.findIndex((m) => m.id === sent.questionId);
        const next = at < 0 ? [...prev, message] : [...prev.slice(0, at + 1), message, ...prev.slice(at + 1)];
        return capChatMessages(next);
      });
      const orgId = user?.schoolId || user?.orgId;
      if (orgId && token) {
        turnNumber.current += 1;
        const question =
          reply.kind === "safeguarding"
            ? ""
            : redactQuestion(sent.question, knownNames(queryClient), slotsOf(reply));
        void api.askNoticeboard
          .saveTurn(orgId, token, {
            conversationId: conversationId.current,
            turn: turnNumber.current,
            question,
            replyKind: reply.kind,
            ...turnDetails(reply, activeMatcher.thresholds),
          })
          .catch((error) => {
            console.error("[Ask Noticeboard] Couldn't save the question", error);
          });
      }
    },
    [navigate, setTask, setLastAsked]
  );

  const send = useCallback(
    (text: string) => {
      const question = text.trim();
      if (!question) return;
      setResumeOffer(null);
      const questionId = nextId.current++;
      setMessages((prev) => capChatMessages([...prev, { id: questionId, role: "user", text: question }]));
      const sent = { question, questionId, pathname, generation: generation.current };
      const sentBy = sessionOf(latest.current.user);
      // Still this chat, and still the person who sent it: logging in as someone else is only
      // counted as a new chat after this render, so check who is logged in too
      const stillCurrent = () =>
        sent.generation === generation.current && sessionOf(latest.current.user) === sentBy;
      setReplying((n) => n + 1);
      turns.current = turns.current
        .then(() => (stillCurrent() ? replyInTurn(sent) : undefined))
        .catch((error) => console.error("[Ask Noticeboard] A reply failed after it arrived", error))
        .finally(() => {
          if (sent.generation === generation.current) setReplying((n) => n - 1);
        });
    },
    [pathname, replyInTurn]
  );

  /** Carry on: asks the task's next missing question (or shows its summary again), picking up exactly where it was left. */
  const carryOn = useCallback(() => {
    const record = resumeOffer;
    if (!record) return;
    const sentGeneration = generation.current;
    const sentBy = sessionOf(latest.current.user);
    const stillCurrent = () =>
      sentGeneration === generation.current && sessionOf(latest.current.user) === sentBy;
    setResumeOffer(null);
    setReplying((n) => n + 1);
    turns.current = turns.current
      .then(async () => {
        if (!stillCurrent()) return;
        const { available, fallbackMatcher, matcherStatus, queryClient, token, user } = latest.current;
        const reply = await resumeTaskReply(record, {
          ctx: { user, token, queryClient, pathname },
          available,
          matcher: (matcherStatus === "ready" && matcher.current) || fallbackMatcher,
          rotation: rotation.current,
        });
        if (!stillCurrent() || !reply) return;
        if (reply.kind === "task") setTask(reply.task);
        const message: ChatMessage = { id: nextId.current++, role: "assistant", reply, revealed: false };
        setMessages((prev) => capChatMessages([...prev, message]));
      })
      .catch((error) => console.error("[Ask Noticeboard] Couldn't carry on with the task", error))
      .finally(() => {
        if (sentGeneration === generation.current) setReplying((n) => n - 1);
      });
  }, [resumeOffer, pathname, setTask]);

  /** Start fresh: the task offered is dropped, and any reply still on its way is dropped with it. */
  const startFresh = useCallback(() => {
    generation.current += 1;
    turns.current = Promise.resolve();
    setReplying(0);
    pendingResume.current = null;
    setResumeOffer(null);
    setTask(null);
    setLastAsked(null);
    parked.current = [];
    const reply: AssistantReply = { kind: "task", text: CANCELLED_REPLY, choices: [], task: null };
    setMessages((prev) =>
      capChatMessages([...prev, { id: nextId.current++, role: "assistant", reply, revealed: false }])
    );
  }, [setTask, setLastAsked]);

  /** An instant search result opened: what was typed and where it went stay in the chat, which closes on the result. */
  const openResult = useCallback(
    (typed: string, { kind, label, href, state }: SearchResult) => {
      const question = typed.trim();
      if (question) {
        const reply: AssistantReply = { kind: "opened", text: openedReply(kind, label) };
        setMessages((prev) =>
          capChatMessages([
            ...prev,
            { id: nextId.current++, role: "user", text: question },
            { id: nextId.current++, role: "assistant", reply, revealed: true },
          ])
        );
      }
      setIsOpen(false);
      navigate(href, { state });
    },
    [navigate]
  );

  const markRevealed = useCallback((id: number) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === id && m.role === "assistant" ? { ...m, revealed: true } : m
      )
    );
  }, []);

  // A crash inside the chat closes it and says so; the next open tries it again
  const snackbar = useOptionalSnackbar();
  const chatCrashed = useCallback(() => {
    setIsOpen(false);
    snackbar?.showMessage({
      message: "Ask Noticeboard ran into a problem and closed. Open it again to try once more.",
      severity: "error",
    });
  }, [snackbar]);

  const contextValue = useMemo(
    () => ({ isAvailable, isReady: canOpen, open }),
    [isAvailable, canOpen, open]
  );

  return (
    <AskNoticeboardContext.Provider value={contextValue}>
      {children}
      {isAvailable && (
        <AskNoticeboardBoundary resetKey={openCount} onError={chatCrashed}>
          <AskNoticeboardModal
            open={isOpen}
            onClose={close}
            messages={messages}
            available={available}
            onSend={send}
            onOpenResult={openResult}
            isReplying={replying > 0}
            onNewChat={clearChat}
            onRevealed={markRevealed}
            onOpenHandoff={openHandoff}
            task={task}
            onConfirmTask={confirmTask}
            lastAsked={lastAskedState}
            resumeOffer={resumeOffer}
            onCarryOn={carryOn}
            onStartFresh={startFresh}
          />
        </AskNoticeboardBoundary>
      )}
      {isAvailable && handoffPath && !isOpen && <BackToChatButton onClick={open} />}
    </AskNoticeboardContext.Provider>
  );
}
